import React, { useContext, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../axios/api';
import '../assets/NewFiles/Css/LoginPage.css';
import { GlobalContext } from '../contexts/GlobalContext';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ReactGA from 'react-ga4';
import { Eye, EyeOff, Mail, Smartphone, Lock, KeyRound, MessageSquare } from 'lucide-react';

const LoginPage = () => {
  const [authMode, setAuthMode] = useState('email'); // 'email', 'phone_pass', 'phone_otp'
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);

  // Form State
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');

  const { setLoggedIn, setProfilePicture, setRole, setUserId, setRedeemedPoints, setCollectedPoints } = useContext(GlobalContext);
  const navigate = useNavigate();

  const startOtpTimer = () => {
    setOtpTimer(60);
    const interval = setInterval(() => {
      setOtpTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendMobileOtp = async () => {
    const cleanPhone = phone.replace(/[^0-9]/g, '');
    if (!cleanPhone || cleanPhone.length !== 10) {
      toast.error('Please enter a valid 10-digit mobile number');
      return;
    }

    setIsSendingOtp(true);
    try {
      const response = await api.post('/api/auth/sendotp', { phoneNumber: cleanPhone });
      toast.success(response.data.message || 'OTP sent successfully via SMS');
      setOtpSent(true);
      startOtpTimer();
    } catch (error) {
      const msg = error.response?.data?.message || 'Failed to send OTP';
      toast.error(msg);
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    let payload = {};

    if (authMode === 'email') {
      if (!email || !password) {
        toast.error('Email and Password are required');
        setIsLoading(false);
        return;
      }
      payload = { userEmail: email.toLowerCase().trim(), password };
    } else if (authMode === 'phone_pass') {
      const cleanPhone = phone.replace(/[^0-9]/g, '');
      if (!cleanPhone || cleanPhone.length !== 10) {
        toast.error('Please enter a valid 10-digit mobile number');
        setIsLoading(false);
        return;
      }
      if (!password) {
        toast.error('Password is required');
        setIsLoading(false);
        return;
      }
      payload = { phoneNumber: cleanPhone, password };
    } else if (authMode === 'phone_otp') {
      const cleanPhone = phone.replace(/[^0-9]/g, '');
      if (!cleanPhone || cleanPhone.length !== 10) {
        toast.error('Please enter a valid 10-digit mobile number');
        setIsLoading(false);
        return;
      }
      if (!otp) {
        toast.error('OTP code is required');
        setIsLoading(false);
        return;
      }
      payload = { phoneNumber: cleanPhone, otp };
    }

    try {
      const response = await api.post('/api/auth/signin', payload);

      if (response.status === 200) {
        toast.success('Login successful');

        ReactGA.event({
          category: 'User',
          action: 'Login Click',
          label: `Login Form - ${authMode}`
        });

        localStorage.setItem('token', response.data.token);

        setLoggedIn(true);
        if (response.data.user) {
          setRole(response.data.user.role_code);
          setUserId(response.data.user.id);
          setProfilePicture(response.data.user.userProfilePic);
          setCollectedPoints(response.data.user.collectedPoints || response.data.user.collected_points || 0);
          setRedeemedPoints(response.data.user.redeemedPoints || response.data.user.redeemed_points || 0);
          navigate('/dashboard/stats');
        } else {
          toast.error('Login failed. Invalid user record.');
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      const msg = error.response?.data?.message || 'Login failed. Please check your credentials.';
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <h2>Welcome Back</h2>
          <p>Sign in to access your Aranyasetu dashboard</p>
        </div>

        {/* Authentication Mode Tabs */}
        <div className="login-tabs">
          <button
            type="button"
            className={`tab-btn ${authMode === 'email' ? 'active' : ''}`}
            onClick={() => setAuthMode('email')}
          >
            <Mail size={16} /> Email
          </button>
          {/* <button
            type="button"
            className={`tab-btn ${authMode === 'phone_pass' ? 'active' : ''}`}
            onClick={() => setAuthMode('phone_pass')}
          >
            <Smartphone size={16} /> Mobile
          </button> */}
          <button
            type="button"
            className={`tab-btn ${authMode === 'phone_otp' ? 'active' : ''}`}
            onClick={() => setAuthMode('phone_otp')}
          >
            <MessageSquare size={16} /> SMS OTP
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Email Input */}
          {authMode === 'email' && (
            <div className="login-form-group">
              <label>Email / Phone Number <span style={{ color: '#ef4444' }}>*</span></label>
              <div className="input-wrapper">
                <Mail className="input-icon" size={18} />
                <input
                  type="text"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@company.com or 999999999"
                  required
                />
              </div>
            </div>
          )}

          {/* Phone Input */}
          {(authMode === 'phone_pass' || authMode === 'phone_otp') && (
            <div className="login-form-group">
              <label>Mobile Number <span style={{ color: '#ef4444' }}>*</span></label>
              <div className="input-wrapper">
                <Smartphone className="input-icon" size={18} />
                <span className="phone-prefix">+91</span>
                <input
                  type="tel"
                  maxLength={10}
                  className="with-prefix"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/[^0-9]/g, ''))}
                  placeholder="9876543210"
                  required
                />
              </div>
            </div>
          )}

          {/* Password Input */}
          {(authMode === 'email' || authMode === 'phone_pass') && (
            <div className="login-form-group">
              <label>Password <span style={{ color: '#ef4444' }}>*</span></label>
              <div className="input-wrapper">
                <Lock className="input-icon" size={18} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
          )}

          {/* OTP Input for Mobile Login */}
          {authMode === 'phone_otp' && (
            <div className="login-form-group">
              <label>SMS Verification OTP <span style={{ color: '#ef4444' }}>*</span></label>
              <div className="otp-row">
                <div className="input-wrapper" style={{ flex: 1 }}>
                  <KeyRound className="input-icon" size={18} />
                  <input
                    type="text"
                    maxLength={6}
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.trim())}
                    placeholder="Enter 6-digit OTP"
                    required
                  />
                </div>
                <button
                  type="button"
                  className="send-otp-btn"
                  onClick={handleSendMobileOtp}
                  disabled={isSendingOtp || otpTimer > 0}
                >
                  {isSendingOtp ? 'Sending...' : otpTimer > 0 ? `Resend (${otpTimer}s)` : otpSent ? 'Resend OTP' : 'Send OTP'}
                </button>
              </div>
            </div>
          )}

          <button type="submit" className="login-submit-btn" disabled={isLoading}>
            {isLoading ? 'Authenticating...' : authMode === 'phone_otp' ? 'Verify & Sign In' : 'Sign In'}
          </button>
        </form>

        <div className="login-footer-links">
          <Link to="/forget-password" className="auth-link">Forgot Password?</Link>
          <Link to="/signup" className="auth-link">Create Account</Link>
        </div>
      </div>
      <ToastContainer />
    </div>
  );
};

export default LoginPage;
